import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Hint} from '@/components/ui/hint'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {PersonPicker} from '@/components/investigacion/person-picker'
import {
  useAccionDeRestriccion,
  useDecidirRestriccion,
  useSeguimientoRestricciones,
  type AccionDeRestriccion,
  type RestriccionSeguimiento,
} from '@/hooks/salud/medicina_laboral/use-conceptos'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'

/** `vencida` en ámbar y no gris: es trabajo pendiente, nunca dada por levantada. */
function estadoTone(estado: RestriccionSeguimiento['estado']): 'green' | 'amber' | 'neutral' {
  if (estado === 'vigente') return 'green'
  return estado === 'vencida' ? 'amber' : 'neutral'
}

/** H07. Ningún examen levanta una restricción solo: se exige sustento o decisión administrativa. */
function DecidirDialog({
  businessId,
  employeeId,
  restriccion,
}: {
  businessId: string
  employeeId: string
  restriccion: RestriccionSeguimiento
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [decision, setDecision] = useState<'sigue_vigente' | 'reemplazada' | 'levantada'>('sigue_vigente')
  const [sustento, setSustento] = useState('')
  const [sustentoError, setSustentoError] = useState<string | undefined>()
  const [administrativa, setAdministrativa] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const decidir = useDecidirRestriccion(businessId, employeeId)
  const exigeSustento = decision === 'levantada' && !administrativa

  async function submit(e: FormEvent) {
    e.preventDefault()
    const err = exigeSustento ? validateRequired(sustento) : null
    setSustentoError(err ?? undefined)
    if (err) return
    setError(null)
    try {
      await decidir.mutateAsync({
        restriccionId: restriccion.id,
        body: {decision, sustento: sustento || null, administrativa},
      })
      setOpen(false)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          setDecision('sigue_vigente')
          setSustento('')
          setSustentoError(undefined)
          setAdministrativa(false)
          setError(null)
        }
      }}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`restriccion-decidir-${restriccion.id}`}
          aria-label={t('restriccion.decidir.actionSobre', {texto: restriccion.texto})}>
          {t('restriccion.decidir.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('restriccion.decidir.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{restriccion.texto}</p>
          <div className="space-y-2" role="radiogroup" aria-label={t('restriccion.decidir.title')}>
            {(['sigue_vigente', 'reemplazada', 'levantada'] as const).map((opcion) => (
              <label key={opcion} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="decision"
                  checked={decision === opcion}
                  data-testid={`restriccion-decision-${opcion}`}
                  onChange={() => {
                    setDecision(opcion)
                    setSustentoError(undefined)
                  }}
                />
                <span>{t(`restriccion.decision.${opcion}`)}</span>
              </label>
            ))}
          </div>
          {decision === 'levantada' && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t('restriccion.decidir.exigeSustento')}</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={administrativa}
                  data-testid="restriccion-administrativa"
                  onChange={(e) => {
                    setAdministrativa(e.target.checked)
                    setSustentoError(undefined)
                  }}
                />
                <span>{t('restriccion.decidir.administrativa')}</span>
              </label>
              {!administrativa && (
                <label className="block space-y-1">
                  <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                    {t('restriccion.decidir.sustentoLabel')}
                  </span>
                  <Input
                    value={sustento}
                    maxLength={2000}
                    data-testid="restriccion-sustento"
                    onChange={(e) => {
                      setSustento(e.target.value)
                      setSustentoError(undefined)
                    }}
                    onBlur={(e) => setSustentoError(validateRequired(e.target.value) ?? undefined)}
                  />
                  <FieldError error={sustentoError} />
                </label>
              )}
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={decidir.isPending}
            submitLabel={t('restriccion.decidir.guardar')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** H08. Para contratista o independiente el responsable llega en blanco, y se dice. */
function AccionDialog({
  businessId,
  employeeId,
  restriccion,
}: {
  businessId: string
  employeeId: string
  restriccion: RestriccionSeguimiento
}) {
  const {t} = useTranslation()
  const propuesta = restriccion.propuesta
  const [open, setOpen] = useState(false)
  const [responsable, setResponsable] = useState<{employee_id?: string}>({})
  const [fecha, setFecha] = useState('')
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const [responsableError, setResponsableError] = useState<string | undefined>()
  const [fechaError, setFechaError] = useState<string | undefined>()
  const [sinAccion, setSinAccion] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const registrar = useAccionDeRestriccion(businessId, employeeId)
  if (!propuesta) return null

  function faltaAlgo(): boolean {
    if (sinAccion) {
      const err = validateRequired(motivo)
      setMotivoError(err ?? undefined)
      return !!err
    }
    const rErr = responsable.employee_id ? null : t('restriccion.accion.responsableRequerido')
    const fErr = validateRequired(fecha)
    setResponsableError(rErr ?? undefined)
    setFechaError(fErr ?? undefined)
    return !!rErr || !!fErr
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (faltaAlgo()) return
    setError(null)
    try {
      await registrar.mutateAsync({
        restriccionId: restriccion.id,
        body: sinAccion
          ? {sin_accion_motivo: motivo}
          : {responsable_employee_id: responsable.employee_id ?? null, fecha_limite: fecha || null},
      })
      setOpen(false)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          setResponsable(propuesta.responsable_employee_id ? {employee_id: propuesta.responsable_employee_id} : {})
          setFecha(propuesta.fecha_limite ?? '')
          setMotivo('')
          setMotivoError(undefined)
          setResponsableError(undefined)
          setFechaError(undefined)
          setSinAccion(false)
          setError(null)
        }
      }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`restriccion-accion-${restriccion.id}`}>
          {t(restriccion.acciones.length > 0 ? 'restriccion.accion.otra' : 'restriccion.accion.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('restriccion.accion.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-foreground" data-testid="restriccion-accion-descripcion">
            {propuesta.descripcion}
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sinAccion}
              data-testid="restriccion-sin-accion"
              onChange={(e) => {
                setSinAccion(e.target.checked)
                setMotivoError(undefined)
              }}
            />
            <span>{t('restriccion.accion.sinAccion')}</span>
          </label>
          {sinAccion ? (
            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('restriccion.accion.motivoLabel')}
              </span>
              <Input
                value={motivo}
                maxLength={2000}
                data-testid="restriccion-sin-accion-motivo"
                onChange={(e) => {
                  setMotivo(e.target.value)
                  setMotivoError(undefined)
                }}
                onBlur={(e) => setMotivoError(validateRequired(e.target.value) ?? undefined)}
              />
              <FieldError error={motivoError} />
            </label>
          ) : (
            <div className="space-y-4">
              {propuesta.responsable_en_blanco && (
                <Hint testId="restriccion-responsable-en-blanco">{t('restriccion.accion.responsableEnBlanco')}</Hint>
              )}
              <div className="space-y-1">
                <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                  {t('restriccion.accion.responsableLabel')}
                </span>
                <PersonPicker
                  businessId={businessId}
                  value={responsable}
                  onChange={(next) => {
                    setResponsable(next)
                    setResponsableError(undefined)
                  }}
                  testId="restriccion-accion-responsable"
                />
                <FieldError error={responsableError} />
              </div>
              <div className="space-y-1">
                <DateField
                  id="restriccion-accion-fecha"
                  label={t('restriccion.accion.fechaLabel')}
                  value={fecha}
                  hint={t('restriccion.accion.fechaHint')}
                  onChange={(next) => {
                    setFecha(next)
                    setFechaError(undefined)
                  }}
                />
                <FieldError error={fechaError} />
              </div>
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={registrar.isPending}
            submitLabel={t('restriccion.accion.guardar')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AccionesDeLaRestriccion({acciones}: {acciones: AccionDeRestriccion[]}) {
  const {t} = useTranslation()
  if (acciones.length === 0) return null
  return (
    <ul className="mt-1 space-y-0.5" data-testid="restriccion-acciones">
      {acciones.map((accion) => (
        <li key={accion.id} className="text-xs text-muted-foreground">
          {t('restriccion.accion.fila', {
            descripcion: accion.descripcion,
            fecha: formatDateOnly(accion.fecha_limite),
            responsable: accion.responsable_nombre ?? '',
          })}
        </li>
      ))}
    </ul>
  )
}

function FilaRestriccion({
  businessId,
  employeeId,
  restriccion,
  avisoDias,
  puedeDecidir,
  puedeAccionar,
}: {
  businessId: string
  employeeId: string
  restriccion: RestriccionSeguimiento
  avisoDias: number
  puedeDecidir: boolean
  puedeAccionar: boolean
}) {
  const {t} = useTranslation()
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`restriccion-${restriccion.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-foreground">{restriccion.texto}</p>
        <p className="text-xs text-muted-foreground">
          {t(`restriccion.clase.${restriccion.clase}`)} ·{' '}
          {restriccion.temporalidad === 'temporal' && restriccion.fecha_fin
            ? t('restriccion.hastaEl', {fecha: formatDateOnly(restriccion.fecha_fin)})
            : t(`restriccion.temporalidad.${restriccion.temporalidad}`)}
        </p>
        {restriccion.verifique_si_continua && (
          <p className="text-xs text-foreground" data-testid={`restriccion-vencida-${restriccion.id}`}>
            {t('restriccion.verifiqueSiContinua')}
          </p>
        )}
        {restriccion.por_vencer && restriccion.fecha_fin && (
          <p className="text-xs text-muted-foreground" data-testid={`restriccion-por-vencer-${restriccion.id}`}>
            {t('restriccion.porVencer', {fecha: formatDateOnly(restriccion.fecha_fin), dias: avisoDias})}{' '}
            <Link className="underline" to={`/businesses/${businessId}/cifras`}>
              {t('restriccion.verLaCifra')}
            </Link>
          </p>
        )}
        {restriccion.sin_emparejar && (
          <p className="text-xs text-muted-foreground" data-testid={`restriccion-sin-emparejar-${restriccion.id}`}>
            {t('restriccion.sinEmparejar')}
          </p>
        )}
        {restriccion.levantada_en && (
          <p className="text-xs text-muted-foreground" data-testid={`restriccion-levantada-${restriccion.id}`}>
            {restriccion.levantada_es_administrativa
              ? t('restriccion.levantadaAdministrativa', {nombre: restriccion.levantada_por_nombre ?? ''})
              : t('restriccion.levantadaCon', {sustento: restriccion.levantada_sustento ?? ''})}
          </p>
        )}
        {restriccion.accion_no_requerida_en && (
          <p className="text-xs text-muted-foreground" data-testid={`restriccion-sin-accion-${restriccion.id}`}>
            {t('restriccion.accion.noRequerida', {
              motivo: restriccion.accion_no_requerida_motivo ?? '',
              nombre: restriccion.accion_no_requerida_por_nombre ?? '',
            })}
          </p>
        )}
        <AccionesDeLaRestriccion acciones={restriccion.acciones} />
      </div>
      <div className="flex items-center gap-2">
        <Tag tone={estadoTone(restriccion.estado)} data-testid={`restriccion-estado-${restriccion.id}`}>
          {t(`restriccion.estado.${restriccion.estado}`)}
        </Tag>
        {/* Misma regla que `ya_decidida` en el núcleo: una vencida aún necesita decidir si continúa. */}
        {puedeDecidir && (restriccion.estado === 'vigente' || restriccion.estado === 'vencida') && (
          <DecidirDialog businessId={businessId} employeeId={employeeId} restriccion={restriccion} />
        )}
        {puedeAccionar && <AccionDialog businessId={businessId} employeeId={employeeId} restriccion={restriccion} />}
      </div>
    </li>
  )
}

/** E1-H07/H08. El silencio nunca se lee como «no hay choque»: sin registro o sin perfil, se dice. */
export function RestriccionesSection({
  businessId,
  employeeId,
  canOperate,
  canDecidir,
  canFirmar,
}: {
  businessId: string
  employeeId: string
  canOperate: boolean
  // Decidir es custodia de salud (§4): el dueño la ve y no la levanta.
  canDecidir: boolean
  // Confirmar la acción del plan es firma: trabajo de la empresa, no un dato clínico.
  canFirmar: boolean
}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const {data, isLoading, isError, error} = useSeguimientoRestricciones(businessId, employeeId)
  if (!canOperate) return null
  if (isLoading) return <Skeleton className="h-16 w-full" />
  return (
    <div className="space-y-3 border-t border-border pt-4" data-testid="restricciones-seguimiento">
      <h3 className="text-sm font-medium text-foreground">{t('restriccion.titulo')}</h3>
      {isError ? (
        /* En salud, pintar el vacío ante un 403 afirmaría que no hay restricciones. */
        <p className="text-sm text-destructive" data-testid="restricciones-error">
          {translateApiError(error)}
        </p>
      ) : !data ? null : (
        <>
          {data.cargos_sin_perfil.map((cargo) => (
            <p
              key={cargo.position_id}
              className="text-xs text-muted-foreground"
              data-testid={`restricciones-sin-perfil-${cargo.position_id}`}>
              {t('restriccion.cargoSinPerfil', {cargo: cargo.cargo})}{' '}
              <Link className="underline" to={`/businesses/${businessId}/cargos/${cargo.position_id}/perfil`}>
                {t('restriccion.verElCargo')}
              </Link>{' '}
              <PorQue label={t('common.porQue')} testId={`restricciones-sin-perfil-porque-${cargo.position_id}`}>
                <span>{t('restriccion.cargoSinPerfilPorque')}</span>
              </PorQue>
            </p>
          ))}
          {data.detalle_reducido ? (
            <p className="text-sm text-muted-foreground" data-testid="restricciones-reducidas">
              {t('restriccion.reducidoModoDios', {total: data.total})}
            </p>
          ) : data.sin_registro ? (
            <p
              className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
              data-testid="restricciones-sin-registro">
              {t('restriccion.sinRegistro')} {t('restriccion.registrarConcepto')}
              <PorQue label={t('common.porQue')} testId="restricciones-sin-registro-porque">
                <span>{t('restriccion.sinRegistroPorque')}</span>
              </PorQue>
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="restricciones-list">
              {data.items.map((restriccion) => (
                <FilaRestriccion
                  key={restriccion.id}
                  businessId={businessId}
                  employeeId={employeeId}
                  restriccion={restriccion}
                  avisoDias={data.aviso_dias}
                  puedeDecidir={canDecidir && !godReadOnly}
                  puedeAccionar={canFirmar && !godReadOnly}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
